import { Container, Sprite, Texture } from 'pixi.js';
import gsap from 'gsap';
import { AudioBus, playUIClick } from '../engine/audio';

export interface ButtonOptions {
  x: number;
  y: number;
  idleKey: string;
  hoverKey: string;
  onClick: () => void;
  audio?: AudioBus;
}

// The button baseline renders at the "hover" size so it always feels bold.
// Hovering grows it a touch more; pressing squishes back to 1.0× for feedback.
// IMPORTANT: these are applied to an INNER Container (`visual`) so that outer
// `this.scale.set(...)` calls from scene code still compose predictably.
const IDLE_VISUAL_SCALE = 1.05;
const HOVER_VISUAL_SCALE = 1.1;
const PRESS_VISUAL_SCALE = 1.0;

export class Button extends Container {
  private idle: Sprite;
  private hover: Sprite;
  private visual: Container;

  constructor(opts: ButtonOptions) {
    super();
    this.position.set(opts.x, opts.y);

    this.visual = new Container();
    this.visual.scale.set(IDLE_VISUAL_SCALE);
    this.addChild(this.visual);

    this.idle = new Sprite(Texture.from(opts.idleKey));
    this.idle.anchor.set(0.5);
    this.hover = new Sprite(Texture.from(opts.hoverKey));
    this.hover.anchor.set(0.5);
    this.hover.visible = false;
    this.visual.addChild(this.idle, this.hover);

    this.eventMode = 'static';
    this.cursor = 'pointer';
    const w = this.idle.width * IDLE_VISUAL_SCALE;
    const h = this.idle.height * IDLE_VISUAL_SCALE;
    this.hitArea = {
      contains: (x: number, y: number): boolean =>
        x >= -w / 2 && x <= w / 2 && y >= -h / 2 && y <= h / 2,
    };

    this.on('pointerover', () => {
      this.idle.visible = false;
      this.hover.visible = true;
      gsap.to(this.visual.scale, { x: HOVER_VISUAL_SCALE, y: HOVER_VISUAL_SCALE, duration: 0.12, ease: 'sine.out' });
    });
    this.on('pointerout', () => {
      this.idle.visible = true;
      this.hover.visible = false;
      gsap.to(this.visual.scale, { x: IDLE_VISUAL_SCALE, y: IDLE_VISUAL_SCALE, duration: 0.12, ease: 'sine.out' });
    });
    this.on('pointerdown', () => {
      gsap.fromTo(
        this.visual.scale,
        { x: HOVER_VISUAL_SCALE, y: HOVER_VISUAL_SCALE },
        { x: PRESS_VISUAL_SCALE, y: PRESS_VISUAL_SCALE, duration: 0.08, yoyo: true, repeat: 1, ease: 'sine.out' }
      );
      playUIClick(opts.audio ?? null);
      opts.onClick();
    });
  }
}
